import React from "react";
import { useState } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Checkbox,
  TableContainer,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableSortLabel,
  TableFooter,
} from "@mui/material";

const SmartTable = ({
  fields,
  rows,
  id,
  canSelect,
  onSelect,
  selectedIds,
  sortableFields,
}) => {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState(selectedIds || []);
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState();

  const getRowId = (row) =>
    id && fields.length ? row[id] : row[fields[0]?.id];

  const handleRequestSort = (property) => {
    if (!property) return;
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const tryParseDate = (value) => {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "number") return null;
    if (typeof value !== "string") return null;

    const iso = Date.parse(value);
    if (!Number.isNaN(iso)) return iso;

    if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
      const parsed = Date.parse(`1970-01-01T${value}Z`);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const getComparator = (ord, ordBy) => {
    if (!ordBy) return () => 0;
    return (a, b) => {
      const va = a?.[ordBy];
      const vb = b?.[ordBy];

      const da = tryParseDate(va);
      const db = tryParseDate(vb);
      let comp = 0;

      if (da !== null && db !== null) {
        comp = da < db ? -1 : da > db ? 1 : 0;
      } else if (typeof va === "number" && typeof vb === "number") {
        comp = va - vb;
      } else if (
        !Number.isNaN(parseFloat(va)) &&
        !Number.isNaN(parseFloat(vb))
      ) {
        comp = parseFloat(va) - parseFloat(vb);
      } else {
        const sva = (va ?? "").toString().toLowerCase();
        const svb = (vb ?? "").toString().toLowerCase();
        comp = sva.localeCompare(svb);
      }

      return ord === "desc" ? -comp : comp;
    };
  };

  const stableSort = (array, comparator) => {
    if (!array) return array;
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const orderRes = comparator(a[0], b[0]);
      if (orderRes !== 0) return orderRes;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((row) => getRowId(row));
      setSelected(newSelecteds);

      if (onSelect) {
        onSelect(newSelecteds);
      }
    } else {
      setSelected([]);

      if (onSelect) {
        onSelect([]);
      }
    }
  };

  const handleSelect = (row, event) => {
    const id = getRowId(row);
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);

    if (onSelect) {
      onSelect(newSelected);
    }
  };

  const header = (
    <TableHead>
      <TableRow>
        {canSelect && (
          <TableCell padding="checkbox" key={"checkbox"}>
            <Checkbox
              indeterminate={
                selected.length > 0 && selected.length < rows?.length
              }
              checked={rows?.length > 0 && selected.length === rows.length}
              onChange={handleSelectAll}
            />
          </TableCell>
        )}
        {fields &&
          fields.map((field, index) => {
            let fieldHeader = field.header;

            if (field.header && typeof field.header === "function") {
              fieldHeader = field.header(field);
            }

            const isSortable =
              !!field.id &&
              field.sortable !== false &&
              (sortableFields ? sortableFields.includes(field.id) : false);

            return (
              <TableCell
                key={index}
                align={field.align ?? "left"}
                padding={field.disablePadding ? "none" : "normal"}
                sortDirection={
                  isSortable && orderBy && orderBy === field.id ? order : false
                }
                sx={field.sx}
              >
                {isSortable ? (
                  <TableSortLabel
                    hideSortIcon
                    active={orderBy && orderBy === field.id}
                    direction={orderBy && orderBy === field.id ? order : "asc"}
                    onClick={() => handleRequestSort(field.id)}
                  >
                    {fieldHeader}
                    {orderBy && orderBy === field.id ? (
                      <Box sx={{ ...visuallyHidden }}>
                        {order === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                ) : (
                  fieldHeader
                )}
              </TableCell>
            );
          })}
      </TableRow>
    </TableHead>
  );

  const sortedRows = orderBy
    ? stableSort(rows, getComparator(order, orderBy))
    : rows;
  const bodyRows =
    rowsPerPage && sortedRows?.length
      ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedRows;

  const body = bodyRows?.length > 0 && (
    <TableBody>
      {bodyRows.map((row, index) => {
        const key = getRowId(row);
        const isItemSelected = selected.indexOf(key) !== -1;

        return (
          <TableRow
            hover
            key={index}
            tabIndex={-1}
            role="checkbox"
            selected={isItemSelected}
            aria-checked={isItemSelected}
          >
            {canSelect && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isItemSelected}
                  onChange={(event) => handleSelect(row, event)}
                />
              </TableCell>
            )}
            {fields &&
              fields.map((field, fieldIndex) => {
                let fieldValue;
                if (field.id) {
                  fieldValue = row[field.id];
                }

                if (field.data && typeof field.data === "function") {
                  fieldValue = field.data(row, fieldValue);
                }

                return (
                  <TableCell
                    key={fieldIndex}
                    align={field.align ?? "left"}
                    padding={field.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === field.id ? order : false}
                    sx={field.sx}
                  >
                    {fieldValue}
                  </TableCell>
                );
              })}
          </TableRow>
        );
      })}
    </TableBody>
  );

  const hasFooter = fields && fields.find((f) => f.footer);

  const footer = hasFooter && (
    <TableFooter>
      <TableRow>
        {canSelect && (
          <TableCell padding="checkbox" key={"checkbox"}>
            <Checkbox
              indeterminate={
                selected.length > 0 && selected.length < rows?.length
              }
              checked={rows?.length > 0 && selected.length === rows.length}
              onChange={handleSelectAll}
            />
          </TableCell>
        )}
        {fields &&
          fields.map((field, fieldIndex) => {
            let fieldValue = field.footer;

            if (field.footer && typeof field.footer === "function") {
              fieldValue = field.footer(field);
            }

            return (
              <TableCell
                key={fieldIndex}
                align={field.align ?? "left"}
                padding={field.disablePadding ? "none" : "normal"}
                sx={field.sx}
              >
                {fieldValue}
              </TableCell>
            );
          })}
      </TableRow>
    </TableFooter>
  );

  return (
    <TableContainer sx={{}}>
      <Table>
        {header}
        {body}
        {footer}
      </Table>
    </TableContainer>
  );
};

export default SmartTable;
